import actions from 'redux/actions'
import { reducers, getState } from 'redux/core'
import { constants, sentry } from 'common/helpers'


let isSDKLoaded = false

const loadFacebookSDK = () => {
  const elId = 'facebook-jssdk'
  const firstScriptTag = document.getElementsByTagName('script')[0]

  if (document.getElementById(elId)) {
    return
  }

  const script = document.createElement('script')

  script.id = elId
  script.src = '//connect.facebook.net/en_US/sdk.js'
  // script.src = '//connect.facebook.net/en_US/sdk/debug.js'

  if (firstScriptTag.parentNode) {
    firstScriptTag.parentNode.insertBefore(script, firstScriptTag)
  }
  else {
    sentry.captureException(`Cannot find parentNode in loadFacebookSDK`, {
      extra: { elId, firstScriptTag, isSDKLoaded },
    })
  }
}

const setAuth = (authResponse) => {
  reducers.models.facebook.setAuth(authResponse)
}

const getLoginStatus = () =>
  new Promise((resolve, reject) => {
    try {
      window.FB.getLoginStatus(({ authResponse, status }) => {
        if (status === 'connected') {
          setAuth(authResponse)
          resolve(authResponse)
        }
        else {
          reject()
        }
      })

      setTimeout(() => {
        reject(new Error('getLoginStatus timeout exceeded.'))
      }, 10000)
    }
    catch (err) {
      reject(err)
    }
  })


let pluginNodesToParse = []

const parsePluginNode = (node) => {
  const method = () => {
    window.FB.XFBML.parse(node)
  }

  if (isSDKLoaded) {
    method()
  }
  else {
    pluginNodesToParse.push(method)
  }
}

const subscribePlugin = (pluginName) => {
  window.FB.Event.subscribe(pluginName, (data) => {
    if (data.event === 'rendered') {
      reducers.models.facebook.updatePlugin({
        name: pluginName,
        data,
      })
    }
    else if (data.event === 'hidden') {
      reducers.models.facebook.removePlugin(pluginName)
    }
  })
}

const init = () => {
  const { models: { facebook: { isInitialized, isFetching, config: { appId } } } } = getState()

  if (!isInitialized && !isFetching) {
    reducers.models.facebook.setFetching(true)

    loadFacebookSDK()

    window.fbAsyncInit = async () => {
      isSDKLoaded = true

      try {
        if (!appId) {
          throw new Error('Empty appId')
        }

        // TODO always check actual version, because it can be deprecated after new release in 90 days
        // https://developers.facebook.com/docs/javascript/quickstart
        window.FB.init({
          appId,
          status: true,
          cookie: true,
          xfbml: true,
          oauth: true,
          version: 'v4.0',
        })

        reducers.models.facebook.setInitialized(true)

        // TODO we need to move subscribe to places where we need such plugin to call ondemand
        // Also we need to add unsubscribe (componentWillUnmount) BUT! we need to pass same handler as in subscribe...
        subscribePlugin('messenger_checkbox')

        pluginNodesToParse.forEach((method) => method())
        pluginNodesToParse = []

        await getLoginStatus()
          .catch((error) => {
            if (error) {
              throw error
            }
          })
          .finally(() => {
            reducers.models.facebook.setFetching(false)
          })
      }
      catch (error) {
        reducers.models.facebook.setInitialized(false)
        reducers.models.facebook.setFetching(false)

        console.log('Facebook error: ', error)
      }
    }
  }
}

const login = (isLoginGQLEnabled) =>
  new Promise((resolve, reject) => {
    window.FB.login(({ authResponse }) => {
      if (authResponse) {
        setAuth(authResponse)
        resolve(authResponse)
      }
      else {
        reject(new Error('Can\'t logged in via Facebook'))
      }
    }, { scope: 'email,user_likes' }) // TODO add user_birthday to scope when it will be approved by fb (ask @andrebrov)
  })
    .then(() => (
      actions.models.user.facebookAuth({
        tokenName: constants.requestTokens.facebookSignin,
        isLoginGQLEnabled,
      })
    ))

const loginWithPermissions = (permissions) =>
  new Promise((resolve) =>
    window.FB.login(resolve, {
      scope: permissions,
    }))

const ensurePermission = (permission) =>
  new Promise((resolve) =>
    window.FB.api('/me/permissions', ({ data }) => {
      if (
        !data
        || data[0][permission] === undefined
        || data[0][permission] === 0
      ) {
        loginWithPermissions(permission)
          .then(resolve)
      }
      else {
        resolve()
      }
    }, () => {
      loginWithPermissions(permission)
        .then(resolve)
    }))

const post = ({ link, message }) => window.FB.api('/me/feed', 'POST', { link, message })

const logEvent = (eventName, pluginName, placement) => {
  const {
    models: {
      me: { id: userId },
      facebook: { config, plugins },
      payment: { orderNumber: ecommerce, subscriptionNumber: subscription },
    },
  } = getState()
  const { user_ref, ref: id, state } = plugins[pluginName] || {} // eslint-disable-line camelcase

  const logData = {
    app_id: config.appId,
    page_id: config.pageId,
    user_ref,
    ref: JSON.stringify({
      id,
      userId,
      placement,
      order: {
        subscription,
        ecommerce,
      },
    }),
  }

  if (state === 'checked') {
    console.log('FbEvent', logData)
    window.FB.AppEvents.logEvent(eventName, null, logData)
  }
}

const sendMessage = (link) => {
  if (window.FB) {
    return window.FB.ui({ method: 'send', link })
  }
}

const share = ({ link, message, hashtag, mobile_iframe }) => window.FB.ui({
  method: 'share',
  href: link,
  quote: message,
  hashtag,
  mobile_iframe,
}, (response) => {})

export default {
  init,
  post,
  parsePluginNode,
  ensurePermission,
  logEvent,
  sendMessage,
  login,
  setAuth,
  share,
}
